import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_JULIA_C } from './fractalPresets';
import { EXPLORER_PALETTE, samplePalette, withPaletteTheme } from './fractalPalette';
import { iterateUntilEscape, traceJuliaOrbit } from './fractalIteration';
import { drawJuliaFractalWebGL } from './fractalWebGL';
import { renderFractalAnimationFrame } from './JuliaAnimationExplorers';

export const ORBIT_START_PRESETS = Object.freeze([
  { id: 'spiral-arm', label: 'Spiral Filament', z0: [-0.42, 0.24] },
  { id: 'near-origin', label: 'Critical Neighborhood', z0: [0.08, -0.05] },
  { id: 'repelling-alpha', label: 'Repelling Fixed Point', z0: [-0.5275, 0.0759] },
  { id: 'fast-escape', label: 'Exterior Escape', z0: [1.15, 0.85] },
]);

const VIEW_RANGE = Object.freeze([-2.2, 2.2]);
const CANVAS_SIZE = 480;

function toPercent(value) {
  return ((value - VIEW_RANGE[0]) / (VIEW_RANGE[1] - VIEW_RANGE[0])) * 100;
}

export function OrbitTrajectoryExplorer({ juliaC = DEFAULT_JULIA_C, paletteTheme }) {
  const [z0, setZ0] = useState(() => [...ORBIT_START_PRESETS[0].z0]);
  const [dragging, setDragging] = useState(false);
  const canvasRef = useRef(null);

  const palette = useMemo(
    () => withPaletteTheme({ ...EXPLORER_PALETTE, gScale: 7, juliaC }, paletteTheme),
    [paletteTheme, juliaC]
  );
  const trajectory = useMemo(
    () => traceJuliaOrbit(z0[0], z0[1], juliaC, 40),
    [z0, juliaC]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent || '') && !canvas.getContext?.mock) {
      return;
    }
    const span = VIEW_RANGE[1] - VIEW_RANGE[0];
    if (
      renderFractalAnimationFrame(canvas, {
        center: [0, 0],
        basisX: [span, 0],
        basisY: [0, -span],
        juliaC,
        isJulia: true,
        gScale: palette?.gScale ?? 7,
        phaseOffset: palette?.phaseOffset ?? 0,
        demStrength: 0.14,
        paletteTheme: paletteTheme || 'amethyst',
      })
    ) {
      return;
    }
    if (drawJuliaFractalWebGL(canvas, VIEW_RANGE, VIEW_RANGE, palette, { zoomTransition: false })) {
      return;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx || typeof ctx.createImageData !== 'function') return;
    const w = canvas.width;
    const h = canvas.height;
    const imageData = ctx.createImageData(w, h);
    if (!imageData?.data) return;
    const data32 = new Uint32Array(imageData.data.buffer);
    const scale = (VIEW_RANGE[1] - VIEW_RANGE[0]) / w;
    const iterResult = { iterations: 0, mu: 0, escaped: false };
    const rgba = [0, 0, 0, 0];
    for (let row = 0; row < h; row++) {
      const y = VIEW_RANGE[0] + (row + 0.5) * scale;
      const rowOffset = row * w;
      for (let col = 0; col < w; col++) {
        const x = VIEW_RANGE[0] + (col + 0.5) * scale;
        iterateUntilEscape(x, y, iterResult, 256, true, juliaC);
        if (iterResult.escaped) {
          samplePalette(iterResult.mu, palette, rgba);
          data32[rowOffset + col] =
            rgba[0] | (rgba[1] << 8) | (rgba[2] << 16) | (rgba[3] << 24);
        }
      }
    }
    ctx.putImageData(imageData, 0, 0);
  }, [palette, paletteTheme, juliaC]);

  const updateFromClientPoint = useCallback((clientX, clientY, targetEl) => {
    const rect = targetEl?.getBoundingClientRect?.();
    if (!rect || !(rect.width > 0 && rect.height > 0)) return;
    const u = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const v = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
    const x = Number((VIEW_RANGE[0] + u * (VIEW_RANGE[1] - VIEW_RANGE[0])).toFixed(4));
    const y = Number((VIEW_RANGE[0] + v * (VIEW_RANGE[1] - VIEW_RANGE[0])).toFixed(4));
    setZ0([x, y]);
  }, []);

  const polylinePoints = trajectory.points
    .map(([x, y]) => `${toPercent(x).toFixed(2)},${toPercent(y).toFixed(2)}`)
    .join(' ');
  const escapeRadiusPercent = (2 / (VIEW_RANGE[1] - VIEW_RANGE[0])) * 100;
  const formatComplex = ([x, y]) => `${x.toFixed(4)} ${y >= 0 ? '+' : '\u2212'} ${Math.abs(y).toFixed(4)}i`;

  return (
    <div className="orbit-explorer" data-testid="orbit-explorer" data-palette-theme={paletteTheme || 'amethyst'}>
      <div className="orbit-explorer-header">
        <span className="orbit-explorer-title">Orbit Trajectory (z<sub>0</sub> &rarr; z<sub>1</sub> &rarr; z<sub>2</sub> &rarr; &hellip;)</span>
        <span className="orbit-explorer-readout" data-testid="orbit-z0-readout">
          z<sub>0</sub> = {formatComplex(z0)}
        </span>
      </div>
      <div className="orbit-explorer-presets" role="group" aria-label="Starting orbit presets">
        {ORBIT_START_PRESETS.map(preset => {
          const active = Math.abs(z0[0] - preset.z0[0]) < 1e-4 && Math.abs(z0[1] - preset.z0[1]) < 1e-4;
          return (
            <button
              key={preset.id}
              type="button"
              className={`parameter-preset-pill${active ? ' active' : ''}`}
              data-orbit-preset={preset.id}
              aria-pressed={active}
              onClick={() => setZ0([...preset.z0])}
            >
              {preset.label}
            </button>
          );
        })}
      </div>
      <div className="orbit-explorer-body">
        <div
          className="orbit-canvas-stage"
          data-testid="orbit-canvas-stage"
          onMouseDown={event => {
            if (event.button !== 0) return;
            setDragging(true);
            updateFromClientPoint(event.clientX, event.clientY, event.currentTarget);
          }}
          onMouseMove={event => {
            if (!dragging) return;
            updateFromClientPoint(event.clientX, event.clientY, event.currentTarget);
          }}
          onMouseUp={() => setDragging(false)}
          onMouseLeave={() => setDragging(false)}
        >
          <canvas
            ref={canvasRef}
            width={CANVAS_SIZE}
            height={CANVAS_SIZE}
            aria-label="Julia orbit trajectory canvas"
          />
          <svg
            className="orbit-overlay-svg"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <circle
              cx="50"
              cy="50"
              r={escapeRadiusPercent}
              className="orbit-escape-circle"
            />
            <polyline
              points={polylinePoints}
              className={`orbit-trajectory-line${trajectory.escaped ? ' escaped' : ' bounded'}`}
            />
            {trajectory.points.map(([x, y], index) => (
              <circle
                key={index}
                cx={toPercent(x)}
                cy={toPercent(y)}
                r={index === 0 ? 1.9 : 1.05}
                className={index === 0 ? 'orbit-point-start' : 'orbit-point-step'}
              />
            ))}
          </svg>
        </div>
        <div className="orbit-telemetry" data-testid="orbit-telemetry">
          <div className="orbit-telemetry-row">
            <span>Outcome</span>
            <strong>
              {trajectory.escaped
                ? `Escapes |z| > 2 (step ${trajectory.escapeStep})`
                : `Bounded (${trajectory.points.length - 1} steps \u2264 2)`}
            </strong>
          </div>
          <div className="orbit-telemetry-row">
            <span>Last |z<sub>n</sub>|</span>
            <code>{trajectory.finalMagnitude.toFixed(4)}</code>
          </div>
          <div className="orbit-telemetry-row">
            <span>Min |z<sub>k</sub>| (critical distance)</span>
            <code>{trajectory.minDistanceToOrigin.toFixed(4)}</code>
          </div>
          <p className="orbit-telemetry-note">
            Drag z<sub>0</sub> across the canvas to watch how points on the spiral filaments wind around the repelling fixed point before crossing the dashed |z| = 2 escape horizon.
          </p>
        </div>
      </div>
    </div>
  );
}
